import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Trans, useTranslation } from 'react-i18next';

import BdsSegment from '../../../../../shared/components/v3/BdsSegment/BdsSegment';
import EmptyCard from '../../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import RankingCard from '../../../../../shared/components/v3/Cards/RankingCard/RankingCard';
import {
  BENEFITS_HOME_TAB,
  CLICK_ACTION,
  HOME_RF_TABS_DICT,
  SECTION_BALANCE_PER_CATEGORY,
  SELECT_TAB_RF
} from '../../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { mapRFConsumptionRankingCardData } from '../../../../../shared/handlers/dataMapper';
import { useRFCompanyConsumption } from '../../../../../shared/hooks/useRFCompanyConsumption/useRFCompanyConsumption';
import { IFlexibleRemunerationPayRollCycle } from '../../../../../shared/interfaces/company';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_HOME } from '../../../../../shared/translations/home';
import { getLocalizedMonthName, toTitleCase } from '../../../../../shared/utils/utils';

import './Ranking.scss';

const Ranking = () => {
  const [tabIndex, setTabIndex] = useState(1);
  const [selectedCycle, setSelectedCycle] = useState<IFlexibleRemunerationPayRollCycle | undefined>();
  const { companyRFCycles, isLoadingRFCycles, errorGettingRFCycles, isLoadingCompanyData } = useContext(CompanyContext);

  const { consumptionData, error, loading } = useRFCompanyConsumption(selectedCycle, !!tabIndex);

  const { t } = useTranslation();

  const rankingCardData = consumptionData?.consumptions || [];

  const isLoading = useMemo(
    () => loading || isLoadingRFCycles || isLoadingCompanyData,
    [loading, isLoadingRFCycles, isLoadingCompanyData]
  );

  useEffect(() => {
    if (companyRFCycles) {
      setSelectedCycle(companyRFCycles.payRollCycles[0]);
    } else {
      setSelectedCycle(undefined);
    }
  }, [companyRFCycles]);

  const tabsItems = [t(TRANSLATIONS_HOME.expensesCard.lastCycle), t(TRANSLATIONS_HOME.expensesCard.currentCycle)];

  const handleTabChange = (index: number) => {
    setTabIndex(index);
    setSelectedCycle(index === 0 ? companyRFCycles?.payRollCycles[1] : companyRFCycles?.payRollCycles[0]);
    TagManagerService.pushEvent(CLICK_ACTION, SELECT_TAB_RF, {
      ...SECTION_BALANCE_PER_CATEGORY,
      ...HOME_RF_TABS_DICT[index]
    });
  };

  const handleHeaderCycle = () => {
    if (errorGettingRFCycles || !companyRFCycles || !selectedCycle) {
      return;
    }
    if (companyRFCycles.payRollCycles.length === 1) {
      const month = toTitleCase(getLocalizedMonthName(selectedCycle.cycleMonth));
      return (
        <div className="ranking-header-cycle">
          <Trans
            i18nKey={TRANSLATIONS_HOME.expensesCard.onlyCycle}
            components={{
              b: <b></b>
            }}
            values={{
              month,
              year: selectedCycle.cycleYear
            }}
          />
        </div>
      );
    }
    return <BdsSegment items={tabsItems} activeIndex={tabIndex} action={handleTabChange} type="secondary" size="sm" />;
  };

  return (
    <RankingCard
      title={t(TRANSLATIONS_HOME.expensesCard.title)}
      valueText={'€'}
      headButton={handleHeaderCycle()}
      data={mapRFConsumptionRankingCardData(consumptionData?.totalBalance ? rankingCardData : [])}
      loading={isLoading}
      error={error || errorGettingRFCycles}
      gtmTab={BENEFITS_HOME_TAB}
      suffixIconType="circle-indicator"
      highlightTopThree={false}
      emptyRankingCard={
        <EmptyCard
          className="whitespace-pre-wrap"
          primaryText={t(TRANSLATIONS_HOME.expensesCard.emptyState.emptyStateTitle)}
          secondaryText={t(TRANSLATIONS_HOME.expensesCard.emptyState.emptyStateDescription)}
        />
      }
    />
  );
};

export default Ranking;
